import { useState, useEffect } from 'react';
import api from '@/api';
import {
  Plus, Trash2, Save, Phone, Mail, AlertTriangle, Wrench, Users,
  Loader2, X, Edit2
} from 'lucide-react';

const CATEGORIES = [
  { value: 'emergency', label: 'Emergency', color: 'bg-rose-100 dark:bg-rose-950/30 text-rose-700 dark:text-rose-400 border-rose-300 dark:border-rose-900' },
  { value: 'service', label: 'Service Provider', color: 'bg-amber-100 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-900' },
  { value: 'general', label: 'General', color: 'bg-muted/50 text-muted-foreground border-border' },
];

const EMPTY_FORM = { name: '', role: '', company: '', phone: '', email: '', category: 'general', notes: '', sort_order: 0 };

export default function ServiceContactsPanel({ projectId }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchContacts = async () => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/contacts`);
      setContacts(res.data || []);
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchContacts(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/closeout/projects/${projectId}/contacts/${editId}`, form);
      } else {
        await api.post(`/closeout/projects/${projectId}/contacts`, form);
      }
      const { toast } = await import('sonner');
      toast.success(editId ? 'Contact updated' : 'Contact added');
      setShowForm(false);
      setEditId(null);
      setForm(EMPTY_FORM);
      fetchContacts();
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await api.delete(`/closeout/projects/${projectId}/contacts/${id}`);
      const { toast } = await import('sonner');
      toast.success('Contact deleted');
      fetchContacts();
    } catch {
      const { toast } = await import('sonner');
      toast.error('Failed to delete');
    }
  };

  const startEdit = (c) => {
    setForm({
      name: c.name || '',
      role: c.role || '',
      company: c.company || '',
      phone: c.phone || '',
      email: c.email || '',
      category: c.category || 'general',
      notes: c.notes || '',
      sort_order: c.sort_order || 0,
    });
    setEditId(c.id);
    setShowForm(true);
  };

  const catIcon = (cat) => {
    if (cat === 'emergency') return <AlertTriangle className="w-3.5 h-3.5 text-rose-500" />;
    if (cat === 'service') return <Wrench className="w-3.5 h-3.5 text-amber-500" />;
    return <Users className="w-3.5 h-3.5 text-muted-foreground" />;
  };

  if (loading) return (
    <div className="flex items-center justify-center h-40">
      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="service-contacts-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-heading uppercase tracking-widest text-primary/80 flex items-center gap-2">
          <Phone className="w-4 h-4" /> Service & Emergency Contacts
        </h3>
        <button onClick={() => { setShowForm(true); setEditId(null); setForm(EMPTY_FORM); }}
          data-testid="add-contact-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider bg-blue-600 hover:bg-blue-700 text-white rounded-sm transition-colors">
          <Plus className="w-3 h-3" /> Add Contact
        </button>
      </div>

      <p className="text-[10px] text-muted-foreground">
        These contacts will appear in the owner's Handover Portal for building operations and emergencies.
      </p>

      {contacts.length === 0 && !showForm ? (
        <div className="flex flex-col items-center justify-center h-32 bg-card border border-dashed border-border rounded-sm">
          <Phone className="w-6 h-6 text-muted-foreground/40 mb-1" />
          <p className="text-[10px] text-muted-foreground">No contacts added yet</p>
          <p className="text-[9px] text-muted-foreground/60 mt-0.5">Add emergency contacts, service providers, and key personnel</p>
        </div>
      ) : (
        <div className="space-y-2">
          {['emergency', 'service', 'general'].map(cat => {
            const catContacts = contacts.filter(c => (c.category || 'general') === cat);
            if (catContacts.length === 0) return null;
            const catInfo = CATEGORIES.find(c => c.value === cat);
            return (
              <div key={cat} className="space-y-1.5">
                <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mt-3 first:mt-0">{catInfo?.label}</p>
                {catContacts.map(c => (
                  <div key={c.id} className="flex items-center gap-3 px-4 py-3 bg-card border border-border rounded-sm group"
                    data-testid={`contact-row-${c.id}`}>
                    {catIcon(c.category)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-foreground font-medium">{c.name}</span>
                        {c.role && <span className="text-[10px] text-muted-foreground">({c.role})</span>}
                        {c.company && <span className="text-[10px] text-muted-foreground/60">{c.company}</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-[10px] text-muted-foreground">
                        {c.phone && <span className="flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{c.phone}</span>}
                        {c.email && <span className="flex items-center gap-1"><Mail className="w-2.5 h-2.5" />{c.email}</span>}
                        {c.notes && <span className="truncate max-w-[200px]">{c.notes}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => startEdit(c)} data-testid={`edit-contact-${c.id}`}
                        className="p-1.5 text-muted-foreground hover:text-primary transition-colors">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(c.id)} data-testid={`delete-contact-${c.id}`}
                        className="p-1.5 text-muted-foreground hover:text-red-500 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="border border-primary/30 bg-card rounded-sm p-4 space-y-3" data-testid="contact-form">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-xs font-heading uppercase tracking-widest text-foreground">
              {editId ? 'Edit Contact' : 'New Contact'}
            </h4>
            <button onClick={() => { setShowForm(false); setEditId(null); setForm(EMPTY_FORM); }}
              className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-1">Name *</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                data-testid="contact-name-input"
                className="w-full px-3 py-2 text-xs bg-background border border-border rounded-sm text-foreground focus:outline-none focus:border-primary/50" />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-1">Role</label>
              <input value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
                placeholder="e.g. Building Engineer, HVAC Tech"
                data-testid="contact-role-input"
                className="w-full px-3 py-2 text-xs bg-background border border-border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50" />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-1">Phone</label>
              <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                data-testid="contact-phone-input"
                className="w-full px-3 py-2 text-xs bg-background border border-border rounded-sm text-foreground focus:outline-none focus:border-primary/50" />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-1">Email</label>
              <input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                data-testid="contact-email-input"
                className="w-full px-3 py-2 text-xs bg-background border border-border rounded-sm text-foreground focus:outline-none focus:border-primary/50" />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-1">Company</label>
              <input value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))}
                data-testid="contact-company-input"
                className="w-full px-3 py-2 text-xs bg-background border border-border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50" />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-1">Category</label>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                data-testid="contact-category-select"
                className="w-full px-3 py-2 text-xs bg-background border border-border rounded-sm text-foreground focus:outline-none focus:border-primary/50">
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-1">Notes</label>
            <input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="e.g. 24/7 availability, contract #ABC-123"
              data-testid="contact-notes-input"
              className="w-full px-3 py-2 text-xs bg-background border border-border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => { setShowForm(false); setEditId(null); setForm(EMPTY_FORM); }}
              className="px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving || !form.name.trim()}
              data-testid="save-contact-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider bg-blue-600 hover:bg-blue-700 text-white rounded-sm transition-colors disabled:opacity-50">
              <Save className="w-3 h-3" /> {saving ? 'Saving...' : editId ? 'Update' : 'Add Contact'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
